/**
 * AI Chatbot Iframe Readiness Detection Service
 *
 * This service provides reliable detection of when the AI chatbot iframe
 * is ready to receive PostMessage communications.
 */

/**
 * Checks if the iframe is ready using Vuex store state
 * @param {Function} rootGetters - Vuex rootGetters for accessing store state
 * @returns {boolean} True if Vuex indicates iframe is ready
 */
const isVuexIframeReady = (rootGetters) => {
  return rootGetters?.['aiChatbot/isIframeReady'] || false
}

/**
 * AI Chatbot Readiness Detection Service
 * Provides methods to detect when AI chatbot iframe is ready
 */
class AIChatbotReadinessService {
  /**
   * Waits for the isIframeReady Vuex state to become true
   * @param {Function} rootGetters - Vuex rootGetters for accessing store state
   * @param {number} maxWaitTime - Maximum time to wait in milliseconds (default: 20000)
   * @returns {Promise<boolean>} True if ready within timeout, false otherwise
   */
  static waitForIframeReady(rootGetters, maxWaitTime = 20000) {
    return new Promise((resolve) => {
      // Check immediately first to avoid unnecessary delay
      if (isVuexIframeReady(rootGetters)) {
        resolve(true)
        return
      }

      const startTime = Date.now()
      const checkInterval = setInterval(() => {
        if (isVuexIframeReady(rootGetters)) {
          clearInterval(checkInterval)
          resolve(true)
        } else if (Date.now() - startTime > maxWaitTime) {
          clearInterval(checkInterval)
          resolve(false)
        }
      }, 100)
    })
  }
}

export default AIChatbotReadinessService
